import React, { Dispatch, SetStateAction, useCallback, useContext, useMemo, useState } from 'react';

import { FindDocumentsInput } from '@amzn/coral_com-amazon-musicpaperrecordkeeperservice';
import clearTokens from '@amzn/meridian-tokens/base/icon/close-medium';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Tooltip from '@amzn/meridian/tooltip';

import { isNil } from 'lodash';
import { RECORD_KEEPER_TENANT_ID, recordKeeperClient } from 'src/api/recordKeeper/RecordKeeperClient';
import { getCategoryWidths, renderSuggestions } from 'src/components/Agreements/SearchBar/utils';
import { SearchContext, TableFilters } from 'src/components/Agreements/SearchContext';
import { AppContext } from 'src/contexts/appContext';

import './SearchBar.scss';

interface ISearchBar {
    setErrorMessage: Dispatch<SetStateAction<string | undefined>>;
    setLoading: Dispatch<SetStateAction<boolean>>;
}

export const SearchBar: React.FC<ISearchBar> = (props) => {
    const { setErrorMessage, setLoading } = props;
    const { userInfo } = useContext(AppContext);
    const { searchIndex, tableFilters, setTableFilters, setSearchResults } = useContext(SearchContext);
    const [searchType, setSearchType] = useState('table');
    const numFilters = useMemo(() => {
        return Object.values(tableFilters).filter((value) => !isNil(value)).length;
    }, [tableFilters]);

    // track the current selection
    const [searchQuery, setSearchQuery] = useState('');

    // track the state of the user's input
    const [searchInput, setSearchInput] = useState('');

    const suggestions = useMemo(() => {
        return searchIndex.search(searchInput).suggestions.sort((a, b) => b.hits - a.hits);
    }, [searchInput]);

    // render all the "category" tags to the same size for visual consistency
    const tagWidth = useMemo(() => {
        return `${getCategoryWidths(suggestions)}ch`;
    }, [suggestions]);

    const executeTextSearch = useCallback((query: string) => {
        setLoading(true);
        recordKeeperClient
            .findDocuments(
                FindDocumentsInput.clone({
                    tenantId: RECORD_KEEPER_TENANT_ID,
                    numResults: 20,
                    text: {
                        searchText: query
                    },
                    username: userInfo.username
                })
            )
            .subscribe({
                next: (value) => {
                    setLoading(false);
                    setSearchResults(value.documents);
                },
                error: (e) => {
                    setErrorMessage(JSON.stringify(e));
                    setLoading(false);
                }
            });
    }, []);

    const clearSearch = useCallback(() => {
        setTableFilters((prev) => {
            const newFilters: TableFilters = {};
            Object.keys(prev).forEach((key) => (newFilters[key] = undefined));
            return newFilters;
        });
        setSearchQuery('');
        setSearchResults(undefined);
    }, [setTableFilters, setSearchQuery]);

    const handleSearchChange = useCallback(
        (label: string) => {
            const delim = label.indexOf(': ');
            const headerName = label.substring(0, delim);
            const value = label.substring(delim + 2);

            setTableFilters((prev) => ({
                ...prev,
                [headerName]: (prev[headerName] ?? []).concat([value])
            }));
        },
        [searchInput]
    );

    const clearButtonTooltip = searchType === 'text' ? 'Clear text search' : 'Clear filters';
    const filterValueLabel = `${numFilters} filter${numFilters !== 1 ? 's' : ''} applied`;
    const placeholderText = 'Search for anything...';

    const filterSelector = (
        <Select
            placeholder={placeholderText}
            onChange={handleSearchChange}
            searchQuery={searchInput}
            onSearch={setSearchInput}
            valueLabel={() => (numFilters > 0 ? filterValueLabel : undefined)}
            popoverMaxHeight={500}
        >
            {renderSuggestions(suggestions, tagWidth)}
        </Select>
    );

    const textSearchBar = (
        <Input
            placeholder={placeholderText}
            value={searchQuery}
            onChange={setSearchQuery}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    executeTextSearch(searchQuery);
                }
            }}
        />
    );

    return (
        <Row widths={['fill']}>
            <Row className={'better-input-group'} spacing={'none'} widths={['fit', 'fill', 'fit']}>
                <Select value={searchType} onChange={setSearchType}>
                    <SelectOption label={'Search in table'} value={'table'} />
                    <SelectOption label={'Search file content'} value={'text'} />
                </Select>
                {searchType === 'text' ? textSearchBar : filterSelector}
                <Tooltip position={'start'} title={clearButtonTooltip}>
                    <Button type={'tertiary'} onClick={clearSearch} aria-label={'clear-search'}>
                        <Icon tokens={clearTokens} />
                    </Button>
                </Tooltip>
            </Row>
        </Row>
    );
};
